import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Auto, Marca, Modelo, TipoVehiculo, Version } from '../../../comun/model/autos.model';
import { AutosApiService } from '../api/autos-api.service';

@Injectable({
  providedIn: 'root'
})
export class AutosBusquedaStoreService {
  private autoSeleccionadoSubject = new BehaviorSubject<Auto>(null);
  autoSeleccionadoSubject$ = this.autoSeleccionadoSubject.asObservable();

  private versionesSubject = new BehaviorSubject<Version[]>([]);
  versionesSubject$ = this.versionesSubject.asObservable();

  private tiposVehiculoSubject = new BehaviorSubject<TipoVehiculo[]>([]);
  tiposVehiculoSubject$ = this.tiposVehiculoSubject.asObservable();

  private marcasDestacadasSubject = new BehaviorSubject<Marca[]>([]);
  marcasDestacadasSubject$ = this.marcasDestacadasSubject.asObservable();

  private marcasSubject = new BehaviorSubject<Marca[]>([]);
  marcasSubject$ = this.marcasSubject.asObservable();

  private modelosDestacadosSubject = new BehaviorSubject<Modelo[]>([]);
  modelosDestacadosSubject$ = this.modelosDestacadosSubject.asObservable();

  private modelosSubject = new BehaviorSubject<Modelo[]>([]);
  modelosSubject$ = this.modelosSubject.asObservable();

  private versionSeleccionadaSubject = new BehaviorSubject<Version>(null);
  versionSeleccionadaSubject$ = this.versionSeleccionadaSubject.asObservable();

  private idTipoVehiculoSeleccionadoSubject = new BehaviorSubject<number>(-1);
  idTipoVehiculoSeleccionadoSubject$ = this.idTipoVehiculoSeleccionadoSubject.asObservable();

  private marcaSeleccionadaSubject = new BehaviorSubject<string>('');
  marcaSeleccionadaSubject$ = this.marcaSeleccionadaSubject.asObservable();

  private modeloSeleccionadoSubject = new BehaviorSubject<string>('');
  modeloSeleccionadoSubject$ = this.modeloSeleccionadoSubject.asObservable();

  constructor(private autosService: AutosApiService) { }

  getVersionesPorMatricula(matricula: string): void {
    this.limpiarVersiones();
    this.autosService.getVersionesPorMatricula(matricula).subscribe({
      next: (auto) => {
        this.autoSeleccionadoSubject.next(auto);
        this.versionesSubject.next(auto.versiones || []);
      },
      error: (err) => console.error('Error recuperando versiones:', err),
    });
  }

  getVersiones(): void {
    this.limpiarVersiones();
    this.autosService.getVersiones(
      this.getIdTipoVehiculoSeleccionado(),
      this.getMarcaSeleccionada(),
      this.getModeloSeleccionado()
    ).subscribe({
      next: (versiones) => {
        this.versionesSubject.next(versiones);
      },
      error: (err) => console.error('Error recuperando versiones:', err),
    });
  }

  getMarcasDestacadas(tipoVehiculo: number): void {
    this.autosService.getMarcasDestacadas(tipoVehiculo).subscribe({
      next: (marcas) => {
        this.marcasDestacadasSubject.next(marcas);
      },
      error: (err) => console.error('Error recuperando marcas:', err),
    });
  }

  getMarcas(tipoVehiculo: number): void {
    this.autosService.getMarcas(tipoVehiculo).subscribe({
      next: (marcas) => {
        this.marcasSubject.next(marcas);
      },
      error: (err) => console.error('Error recuperando marcas:', err),
    });
  }

  getModelosDestacados(tipoVehiculo: number, idMarca: number): void {
    this.autosService.getModelosDestacados(tipoVehiculo, idMarca).subscribe({
      next: (modelos) => {
        this.modelosDestacadosSubject.next(modelos);
      },
      error: (err) => console.error('Error recuperando marcas:', err),
    });
  }

  getModelos(tipoVehiculo: number, nombreMarca: string): void {
    this.autosService.getModelos(tipoVehiculo, nombreMarca).subscribe({
      next: (modelos) => {
        this.modelosSubject.next(modelos);
      },
      error: (err) => console.error('Error recuperando marcas:', err),
    });
  }

  getTiposVehiculo(): void {
    this.autosService.getTiposVehiculo().subscribe({
      next: (tiposVehiculo) => {
        this.tiposVehiculoSubject.next(tiposVehiculo);
      },
      error: (err) => console.error('Error recuperando tipos de vehiculo:', err),
    });
  }

  limpiarDatos(): void {
    this.versionesSubject.next([]);
    this.tiposVehiculoSubject.next([]);
    this.marcasDestacadasSubject.next([]);
    this.marcasSubject.next([]);
    this.modelosDestacadosSubject.next([]);
    this.modelosSubject.next([]);
    this.idTipoVehiculoSeleccionadoSubject.next(-1);
    this.marcaSeleccionadaSubject.next('');
  }

  limpiarMarcas(): void {
    this.marcasDestacadasSubject.next([]);
    this.marcasSubject.next([]);
    this.marcaSeleccionadaSubject.next('');
    this.idTipoVehiculoSeleccionadoSubject.next(-1);
  }

  limpiarModelos(): void {
    this.modelosDestacadosSubject.next([]);
    this.modelosSubject.next([]);
    this.modeloSeleccionadoSubject.next('');
    this.marcaSeleccionadaSubject.next('');
  }

  limpiarVersiones(): void {
    this.autoSeleccionadoSubject.next(null);
    this.versionSeleccionadaSubject.next(null);
    this.versionesSubject.next([]);
  }

  setIdTipoVehiculoSeleccionado(idTipoVehiculo: number): void {
    this.idTipoVehiculoSeleccionadoSubject.next(idTipoVehiculo);
  }

  setMarcaSeleccionada(marca: string): void {
    this.marcaSeleccionadaSubject.next(marca);
  }

  setModeloSeleccionado(modelo: string): void {
    this.modeloSeleccionadoSubject.next(modelo);
  }

  getIdTipoVehiculoSeleccionado(): number {
    return this.idTipoVehiculoSeleccionadoSubject.getValue();
  }

  getMarcaSeleccionada(): string {
    return this.marcaSeleccionadaSubject.getValue();
  }
  
  getModeloSeleccionado(): string {
    return this.modeloSeleccionadoSubject.getValue();
  }

  setVersionSeleccionada(version: Version): void { 
    this.versionSeleccionadaSubject.next(version);
  }

  setAutoSeleccionado (auto: Auto){
    this.autoSeleccionadoSubject.next(auto);
  }
  existeVersionSeleccionada(): boolean {
    // Retorna true solo si hay un objeto Version distinto de null
    return this.versionSeleccionadaSubject.getValue() !== null;
  }
}